import { useEffect, useState } from "react";
import { DownloadSimple, X, AppleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * "アプリに切替" install button.
 *
 * Behaviour:
 *  - Already running standalone (PWA installed) → renders nothing.
 *  - Chrome / Android / Edge: captures `beforeinstallprompt` and calls
 *    `prompt()` on click for the native install dialog.
 *  - iOS Safari: shows a step-by-step instruction modal because Apple
 *    blocks the prompt API and only allows manual "Add to Home Screen".
 */
export default function InstallAppButton({ className = "", testid = "install-app-btn" }) {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showIosHelp, setShowIosHelp] = useState(false);

    const isStandalone = typeof window !== "undefined" && (
        window.matchMedia("(display-mode: standalone)").matches ||
        // iOS Safari sets navigator.standalone on home-screen launches
        window.navigator.standalone === true
    );
    const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

    useEffect(() => {
        // Capture the deferred prompt event raised by Chromium-based browsers
        // when the site meets PWA install criteria. Stash it for later use.
        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        window.addEventListener("beforeinstallprompt", handler);
        return () => window.removeEventListener("beforeinstallprompt", handler);
    }, []);

    if (isStandalone) return null;  // Already installed → button is meaningless

    const onClick = async () => {
        if (deferredPrompt) {
            // Native Chromium / Android flow
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            if (outcome === "accepted") {
                toast.success("ホーム画面に追加されました");
            }
            setDeferredPrompt(null);
        } else if (isIos) {
            setShowIosHelp(true);
        } else {
            // Desktop or unsupported browser — show graceful explanation
            toast.info("このブラウザではアプリ化未対応です。Chrome/Edge/Safariからアクセスしてください。");
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={onClick}
                className={`prc-btn-primary inline-flex items-center gap-2 ${className}`}
                data-testid={testid}
                title="ホーム画面にアプリとして追加"
            >
                <DownloadSimple size={16} />アプリに切替
            </button>

            {showIosHelp && (
                <div className="fixed inset-0 bg-black/70 z-[70] flex items-center justify-center p-4" data-testid="ios-install-help">
                    <div className="prc-card p-5 max-w-md w-full">
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <AppleLogo size={24} className="text-[var(--prc-green)]" />
                                <p className="prc-label-overline mb-0">// INSTALL ON iOS</p>
                            </div>
                            <button onClick={() => setShowIosHelp(false)} aria-label="閉じる" className="text-[var(--prc-muted)] hover:text-white" data-testid="ios-help-close">
                                <X size={24} />
                            </button>
                        </div>
                        <h2 className="text-lg font-bold mb-3">iPhone / iPad にインストール</h2>
                        <ol className="space-y-3 text-sm">
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--prc-green)]/20 text-[var(--prc-green)] flex items-center justify-center font-bold text-xs">1</span>
                                <span>Safari の<strong>「共有」ボタン</strong>（画面下部の <span className="font-mono">⬆︎</span>）をタップ</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--prc-green)]/20 text-[var(--prc-green)] flex items-center justify-center font-bold text-xs">2</span>
                                <span>メニューを下にスクロールして <strong>「ホーム画面に追加」</strong> をタップ</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--prc-green)]/20 text-[var(--prc-green)] flex items-center justify-center font-bold text-xs">3</span>
                                <span>右上の <strong>「追加」</strong> をタップ完了。ホーム画面のアイコンから直接起動できます</span>
                            </li>
                        </ol>
                        <p className="text-[10px] font-mono uppercase tracking-wider text-[var(--prc-muted)] mt-4">
                            // ※ Chrome/Edge では URL バー右側のインストールアイコンから自動追加できます
                        </p>
                        <div className="flex justify-end mt-4">
                            <button onClick={() => setShowIosHelp(false)} className="prc-btn-ghost" data-testid="ios-help-ok">わかりました</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
